// =====================================================================================
// INVRT Radar - web console - one site - Excluded IPs tab
//
// The addresses whose visits this site never identifies (docs/API-CONTRACTS-C1.md §10;
// routes GET/POST/DELETE /sites/:id/excluded-ips in routes/sites.ts). The tenant story is
// simple: your own office and VPN traffic must not use up your identifications.
//
// SHAPES, read from the handler rather than the brief:
//   GET    → {site_id, excluded_ips: [{ipAddress, note?}]}  (no "added" timestamp exists
//            on the wire, so none is shown)
//   POST   → {ips: [{ip_address, note?}]}  - the key is `ips`, 1–1000 entries, note ≤ 500
//   DELETE → {ip_address}
// IPv4 dotted quads only: the server's ipv4HostSchema refuses anything else, and the same
// rule is applied here first so a person sees which lines are wrong before a round trip.
//
// GET is a site-scoped read any member may make; add and remove are organisation-admin
// actions on the server and are gated the same way here.
// =====================================================================================

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { pluralise } from "@/lib/format";
import type { Site } from "@/lib/types";
import type {
  AddExcludedIpsRequest,
  AddExcludedIpsResponse,
  ExcludedIpList,
  RemoveExcludedIpRequest,
  RemoveExcludedIpResponse,
} from "@/lib/types.c1";

/**
 * The server's own rule (the ingest side's IPv4 host grammar): four octets 0–255, no CIDR
 * suffix, nothing else.
 */
const IPV4_HOST_RE =
  /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

const NOTE_MAX_LENGTH = 500;
const MAX_PER_CALL = 1000;

interface ParsedAddresses {
  entries: AddExcludedIpsRequest["ips"];
  /** The raw lines that are not a dotted-quad IPv4 address, in the order typed. */
  invalid: string[];
}

/** One address per line, an optional note after the first comma; blank lines are skipped. */
function parseAddressLines(raw: string): ParsedAddresses {
  const entries: AddExcludedIpsRequest["ips"] = [];
  const invalid: string[] = [];
  for (const line of raw.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (trimmed === "") continue;
    const comma = trimmed.indexOf(",");
    const address = (comma === -1 ? trimmed : trimmed.slice(0, comma)).trim();
    const note = comma === -1 ? "" : trimmed.slice(comma + 1).trim();
    if (!IPV4_HOST_RE.test(address)) {
      invalid.push(address === "" ? trimmed : address);
      continue;
    }
    entries.push(
      note === "" ? { ip_address: address } : { ip_address: address, note: note.slice(0, NOTE_MAX_LENGTH) },
    );
  }
  return { entries, invalid };
}

export function ExcludedIpsPanel({ site }: { site: Site }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId, activeMembership } = useSession();
  const { push } = useToast();
  const [adding, setAdding] = useState(false);
  const [raw, setRaw] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);

  const listKey = ["excluded-ips", orgId, site.id];
  const list = useQuery({
    queryKey: listKey,
    queryFn: () => api.get<ExcludedIpList>(`/sites/${site.id}/excluded-ips`),
    enabled: Boolean(orgId),
  });

  const parsed = useMemo(() => parseAddressLines(raw), [raw]);
  const tooMany = parsed.entries.length > MAX_PER_CALL;

  const add = useMutation({
    mutationFn: () => {
      const body: AddExcludedIpsRequest = { ips: parsed.entries };
      return api.post<AddExcludedIpsResponse>(`/sites/${site.id}/excluded-ips`, body);
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: listKey });
      setAdding(false);
      setRaw("");
      push({
        tone: "success",
        title: "Addresses excluded",
        body: `${pluralise(result.added, "address", "addresses")} added. Visits from them are no longer identified.`,
      });
    },
  });

  const remove = useMutation({
    mutationFn: (ipAddress: string) => {
      const body: RemoveExcludedIpRequest = { ip_address: ipAddress };
      return api.delete<RemoveExcludedIpResponse>(`/sites/${site.id}/excluded-ips`, body);
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: listKey });
      setRemoving(null);
      push({
        tone: "info",
        title: "Address removed",
        body: `Visits from ${result.removed} can be identified again from now on.`,
      });
    },
  });

  const isAdmin = activeMembership?.is_org_admin === true;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>Excluded IPs</CardTitle>
            {isAdmin ? (
              <Button size="sm" onClick={() => setAdding(true)}>
                Add addresses
              </Button>
            ) : null}
          </div>
          <p className="text-sm text-ink-muted">
            Visits from these addresses are never identified, so your own office and VPN
            traffic does not use up your identifications.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {list.isLoading ? <LoadingState label="Loading excluded addresses…" /> : null}
          {list.isError ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          ) : null}
          {list.isSuccess ? (
            list.data.excluded_ips.length === 0 ? (
              <EmptyState
                title="No addresses excluded yet"
                body={
                  isAdmin
                    ? "Add your office and VPN addresses so your own team's visits are not identified or counted."
                    : "An administrator on your team can add your office and VPN addresses here."
                }
              />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Address</TableHeaderCell>
                    <TableHeaderCell>Note</TableHeaderCell>
                    {isAdmin ? <TableHeaderCell className="text-right">Actions</TableHeaderCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.data.excluded_ips.map((entry) => (
                    <TableRow key={entry.ipAddress}>
                      <TableCell className="font-mono text-xs">{entry.ipAddress}</TableCell>
                      <TableCell className="text-ink-muted">{entry.note ?? "No note"}</TableCell>
                      {isAdmin ? (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Remove ${entry.ipAddress}`}
                            onClick={() => setRemoving(entry.ipAddress)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : null}
          {!isAdmin && list.isSuccess && list.data.excluded_ips.length > 0 ? (
            <p className="text-sm text-ink-muted">
              An administrator on your team can add or remove addresses.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Dialog
        open={adding}
        onClose={() => setAdding(false)}
        title="Add addresses"
        description="Visits from these addresses stop being identified the moment they are saved."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => add.mutate()}
              loading={add.isPending}
              disabled={parsed.entries.length === 0 || parsed.invalid.length > 0 || tooMany}
            >
              {parsed.entries.length > 0
                ? `Exclude ${pluralise(parsed.entries.length, "address", "addresses")}`
                : "Exclude addresses"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Textarea
            label="IPv4 addresses"
            value={raw}
            onChange={(event) => setRaw(event.target.value)}
            placeholder={"203.0.113.9, Head office\n198.51.100.4, VPN"}
            hint="One address per line. To add a note, put a comma after the address. Only IPv4 addresses in dotted-quad form are accepted, without a range suffix."
            rows={6}
            {...(parsed.invalid.length > 0
              ? { error: `These lines are not IPv4 addresses: ${parsed.invalid.join(", ")}` }
              : tooMany
                ? { error: `Add at most ${MAX_PER_CALL.toLocaleString("en-GB")} addresses at a time.` }
                : {})}
          />
          {add.isError ? <ErrorState error={add.error} /> : null}
        </div>
      </Dialog>

      <Dialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={`Stop excluding ${removing ?? "this address"}?`}
        description="Visits from this address will be identified again from now on, and will count towards your identifications."
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemoving(null)}>
              Keep it excluded
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => removing && remove.mutate(removing)}
            >
              Remove it
            </Button>
          </>
        }
      >
        {remove.isError ? <ErrorState error={remove.error} /> : null}
      </Dialog>
    </div>
  );
}
